import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { useScrollToTop, scrollToTop } from "@/hooks/useScrollToTop";
import { categories } from "@/mocks/categories";
import { works } from "@/mocks/works";
import { SITE } from "@/config/site";

export default function Category() {
  useScrollToTop();
  const { id } = useParams();
  const navigate = useNavigate();

  const activeCat = categories.find((c) => c.id === id) || categories[0];
  const catWorks = works.filter((w) => w.categoryId === activeCat.id);
  const allWorks = catWorks.length > 0 ? catWorks : works;

  const catJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${activeCat.name}动态漫画 - 抖漫`,
      url: `${SITE.baseUrl}/category/${activeCat.id}`,
      description: `${activeCat.name}动态漫画在线看，抖漫聚合${activeCat.name}题材热门作品，竖屏全屏有声特效沉浸观看。`,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.baseUrl },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE.baseUrl },
        {
          "@type": "ListItem",
          position: 2,
          name: activeCat.name,
          item: `${SITE.baseUrl}/category/${activeCat.id}`,
        },
      ],
    },
  ];

  useSeo({
    title: `${activeCat.name}动态漫画在线看_抖漫${activeCat.name}推荐 - 抖漫`,
    description: `${activeCat.name}动态漫画在线看，抖漫聚合${activeCat.name}题材热门作品，竖屏全屏有声特效沉浸观看，热门作品持续更新，追更不迷路。`,
    keywords: `抖漫${activeCat.name},${activeCat.name}动态漫画,${activeCat.name}漫画在线看`,
    path: `/category/${activeCat.id}`,
    jsonLd: catJsonLd,
  });

  const handleSwitch = (cid: string) => {
    navigate(`/category/${cid}`);
    scrollToTop();
  };

  return (
    <Layout>
      {/* Category banner */}
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={activeCat.cover}
            alt={`${activeCat.name} 动态漫画`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/70 to-background-950"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: activeCat.name }]}
            />
          </Reveal>
          <Reveal delay={80}>
            <div className="flex items-center gap-3 mb-3 mt-3">
              <span className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center text-background-950 text-xl">
                <i className={activeCat.icon}></i>
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-50">
                {activeCat.name}动态漫画
              </h1>
            </div>
            <p className="text-sm md:text-base text-foreground-300 max-w-xl">{activeCat.desc}</p>
          </Reveal>
        </div>
      </section>

      {/* Category switcher */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 -mt-2">
        <Reveal delay={120}>
          <div className="rounded-xl bg-background-900/80 border border-background-200/20 px-1 py-1 flex flex-wrap gap-1">
            {categories.map((c) => {
              const active = c.id === activeCat.id;
              return (
                <button
                  key={c.id}
                  onClick={() => handleSwitch(c.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-primary-500 text-background-950 font-medium"
                      : "bg-transparent text-foreground-400 hover:text-foreground-100"
                  }`}
                >
                  <i className={c.icon}></i>
                  {c.name}
                </button>
              );
            })}
          </div>
        </Reveal>
      </section>

      {/* Works grid */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-10">
        <Reveal delay={160}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-50">
              {activeCat.name}作品
              <span className="ml-2 text-sm font-normal text-foreground-600">{allWorks.length}部</span>
            </h2>
            <div className="flex items-center gap-1 text-sm text-foreground-600">
              <i className="ri-arrow-left-right-line"></i>
              点击上方分类切换
            </div>
          </div>

          {allWorks.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {allWorks.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center text-foreground-500">
              <i className="ri-inbox-line text-4xl mb-3 block"></i>
              <p>该分类暂无作品，敬请期待</p>
            </div>
          )}
        </Reveal>

        {/* Related articles */}
        <div className="mt-14 pt-4 border-t border-background-200/20">
          <Reveal>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-50 mb-6">相关资讯</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "最新更新", to: "/category/xuanhuan" },
                { label: "全部作品", to: "/category/dushi" },
                { label: "关于我们", to: "/about" },
              ].map((l, idx) => (
                <Link
                  key={idx}
                  to={l.to}
                  onClick={scrollToTop}
                  className="rounded-xl bg-background-900/70 border border-background-200/20 p-5 flex items-center justify-between group"
                >
                  <span className="text-foreground-100 group-hover:text-primary-400 transition-colors">
                    {l.label}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-600 group-hover:text-primary-400"></i>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}